import { CheckFilledCvna } from '@carvana/ds-icons';
import Spinner from '@carvana/ds-spinner';
import { icon } from '@carvana/ds-styles';
import React, { useContext, useEffect } from 'react';
import { useBrandTokens } from '|/brand';
import LinkButton from '|/components/carvana-ds/link-button';
import type { PageEventContextValue } from '|/context';
import FormStateContext from '|/context/form-state-context';
import AnimatedFold from './animated-fold';
import styles from './checkout-accordion.module.css';
import HeightCoordinatorContext from './height-coordinator-context';

export interface AccordionStepData {
  url: string;
  title: string;
  isComplete: boolean;
  summary?: string;
  summaryContent?: React.ReactNode;
}

interface AccordionStepProps {
  step: AccordionStepData;
  isAccessible: boolean;
  isFuture: boolean;
  isExpanded: boolean;
  isNextStep: boolean;
  isExiting: boolean;
  onToggle: () => void;
  pageEventScope?: PageEventContextValue | null;
  stepRef?: (el: HTMLDivElement | null) => void;
  children?: React.ReactNode;
}

const AccordionStep: React.FC<AccordionStepProps> = ({
  step,
  isAccessible,
  isFuture,
  isExpanded,
  isNextStep,
  isExiting,
  onToggle,
  pageEventScope,
  stepRef,
  children
}) => {
  const { isLoading } = useContext(FormStateContext);
  const { reportLoading } = useContext(HeightCoordinatorContext);
  const tokens = useBrandTokens();

  useEffect(() => {
    reportLoading(step.url, isLoading);
  }, [step.url, isLoading, reportLoading]);

  useEffect(() => {
    return () => {
      reportLoading(step.url, false);
    };
  }, [step.url, reportLoading]);

  const showButton = isAccessible && !isExpanded;
  const buttonLabel = isNextStep ? 'Continue' : 'Edit';
  const hasSummary = step.summaryContent != null || step.summary != null;
  const analyticsUrl = step.url.replace(/^_/, '');

  return (
    <div
      ref={stepRef}
      className={styles.item}
      data-analytics-scope={pageEventScope ? JSON.stringify(pageEventScope) : undefined}
    >
      <div
        className={styles.header}
        role="button"
        tabIndex={isAccessible ? 0 : -1}
        aria-expanded={isExpanded}
        onClick={
          isAccessible
            ? (e: React.MouseEvent<HTMLDivElement>) => {
                if ((e.target as HTMLElement).closest('button')) return;
                onToggle();
              }
            : undefined
        }
        onKeyDown={
          isAccessible
            ? (e: React.KeyboardEvent<HTMLDivElement>) => {
                if (e.key !== 'Enter' && e.key !== ' ') return;
                if ((e.target as HTMLElement).closest('button')) return;
                e.preventDefault();
                onToggle();
              }
            : undefined
        }
        style={isAccessible ? { cursor: 'pointer' } : undefined}
        data-analytics-id={
          isAccessible ? `checkout-accordion-step-header-${analyticsUrl}` : undefined
        }
      >
        <div className={styles.headerLeft}>
          <div className={styles.titleGroup}>
            <span className={`${styles.title}${!isAccessible ? ` ${styles.titleDisabled}` : ''}`}>
              {step.isComplete && (
                <span className={styles.checkIcon}>
                  <CheckFilledCvna
                    size={16}
                    color={isFuture ? icon.disabled : tokens.completeIcon}
                  />
                </span>
              )}
              {step.title}
            </span>
            {hasSummary && (
              <AnimatedFold isOpen={!isExpanded}>
                {step.summaryContent ? (
                  <div
                    className={`${styles.summaryContent}${!isAccessible ? ` ${styles.summaryContentDisabled}` : ''}`}
                  >
                    {step.summaryContent}
                  </div>
                ) : (
                  <span className={`${styles.summary} ${styles.summaryContent}`}>
                    {step.summary}
                  </span>
                )}
              </AnimatedFold>
            )}
          </div>
        </div>
        {showButton && (
          <LinkButton
            className={styles.actionButton}
            size="sm"
            underline={false}
            onClick={() => {
              onToggle();
            }}
            data-analytics-id={`checkout-accordion-step-${isNextStep ? 'continue' : 'edit'}-${analyticsUrl}`}
            data-analytics-scope={pageEventScope ? JSON.stringify(pageEventScope) : undefined}
          >
            {buttonLabel}
          </LinkButton>
        )}
        {isExpanded && isLoading && <Spinner size={16} />}
      </div>
      <AnimatedFold isOpen={isExpanded && children != null}>
        <div
          className={styles.body}
          style={isExiting ? { opacity: 0.25, transition: 'opacity 500ms ease' } : undefined}
        >
          {children}
        </div>
      </AnimatedFold>
    </div>
  );
};

export default AccordionStep;
